import { Box, Button, Paper, Typography, TextField, Chip } from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import * as services from "../../services/services";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import DehazeIcon from "@mui/icons-material/Dehaze";

const OwnerEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [pets, setPets] = useState([]);

  useEffect(() => {
    fetchOwner();
  }, []);

  const fetchOwner = async () => {
    try {
      const res = await services.getOwnerById(id);
      const data = res.data.data;
      setOwner(data);
      setPets(data.pets || []);
      setImagePreview(data.imageUrl);
    } catch (err) {
      toast.error("โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (file) => {
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleUpdate = async () => {
    try {
      let imageUrl = owner.imageUrl;

      if (imageFile) {
        const uploadRes = await services.uploadImage(imageFile, "owner");
        imageUrl = uploadRes.data.url;
      }

      await services.updateOwner(id, {
        ...owner,
        imageUrl,
      });

      toast.success("แก้ไขสำเร็จ");
      navigate("/owner/list");
    } catch (err) {
      console.log(err);
      toast.error("แก้ไขไม่สำเร็จ");
    }
  };

  const petColumns = [
    { field: "no", headerName: "No", width: 70 },

    { field: "hospitalPetCode", headerName: "รหัสสัตว์", flex: 1 },

    { field: "name", headerName: "ชื่อ", flex: 1 },

    {
      field: "type",
      headerName: "ประเภท",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value || "-"}
          size="small"
          color={params.value === "Dog" ? "primary" : "secondary"}
        />
      ),
    },

    { field: "breed", headerName: "สายพันธุ์", flex: 1 },

    {
      field: "gender",
      headerName: "เพศ",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value || "-"}
          size="small"
          color={params.value === "Male" ? "info" : "error"}
        />
      ),
    },

    {
      field: "birthdate",
      headerName: "อายุ",
      flex: 1,
      renderCell: (params) => {
        if (!params.value) return "-";
        const years = dayjs().diff(dayjs(params.value), "year");
        return `${years} ปี`;
      },
    },

    {
      field: "neutered",
      headerName: "ทำหมัน",
      flex: 1,
      renderCell: (params) => {
        if (!params.value) return "-";

        const isNeutered =
          params.value === "yes" ||
          params.value === "YES" ||
          params.value === "true";

        return (
          <Chip
            label={isNeutered ? "ทำหมันแล้ว" : "ยังไม่ทำหมัน"}
            size="small"
            color={isNeutered ? "success" : "error"}
          />
        );
      },
    },

    {
      field: "microchipNumber",
      headerName: "ไมโครชิป",
      flex: 1.2,
    },

    {
      field: "detail",
      headerName: "ดูข้อมูล",
      width: 120,
      renderCell: (params) => (
        <Button
          size="small"
          variant="text"
          startIcon={<DehazeIcon />}
          onClick={(e) => {
            e.stopPropagation(); // กัน row click ซ้ำ
            navigate(`/pets/${params.row.id}`);
          }}
          sx={{
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          ดูข้อมูล
        </Button>
      ),
    },
  ];

  const petRows = pets.map((pet, index) => ({
    ...pet,
    id: pet.id,
    no: index + 1,
  }));

  if (loading || !owner) return <div>Loading...</div>;

  return (
    <>
      <AppBreadcrumbs />
      <Toaster position="bottom-left" />

      <Box sx={{ maxWidth: 1400, mx: "auto", mt: 3 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
            mb: 4,
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={3}>
            ข้อมูลเจ้าของ
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
              gap: 4,
            }}
          >
            <Box
              sx={{
                border: "2px dashed #ddd",
                borderRadius: 3,
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                minHeight: 280,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fafafa",
              }}
              onClick={() =>
                document.getElementById("edit-owner-image")?.click()
              }
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="owner"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 260,
                    borderRadius: 12,
                  }}
                />
              ) : (
                <Typography color="text.secondary">
                  คลิกเพื่ออัปโหลดรูป
                </Typography>
              )}

              <input
                id="edit-owner-image"
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files?.length) {
                    handleFile(e.target.files[0]);
                  }
                }}
              />
            </Box>

            <Box>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <TextField
                  label="ชื่อ"
                  value={owner.firstName || ""}
                  onChange={(e) =>
                    setOwner({ ...owner, firstName: e.target.value })
                  }
                  fullWidth
                />

                <TextField
                  label="นามสกุล"
                  value={owner.lastName || ""}
                  onChange={(e) =>
                    setOwner({ ...owner, lastName: e.target.value })
                  }
                  fullWidth
                />
              </Box>

              <TextField
                label="เลขบัตรประชาชน"
                fullWidth
                sx={{ mt: 2 }}
                value={owner.idCard || ""}
                onChange={(e) => setOwner({ ...owner, idCard: e.target.value })}
              />

              <TextField
                label="เบอร์โทร"
                fullWidth
                sx={{ mt: 2 }}
                value={owner.phoneNumber || ""}
                onChange={(e) =>
                  setOwner({ ...owner, phoneNumber: e.target.value })
                }
              />

              <TextField
                label="ที่อยู่"
                fullWidth
                multiline
                rows={2}
                sx={{ mt: 2 }}
                value={owner.address || ""}
                onChange={(e) =>
                  setOwner({ ...owner, address: e.target.value })
                }
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 2,
                  mt: 2,
                }}
              >
                <TextField
                  label="ตำบล"
                  value={owner.subDistrict || ""}
                  onChange={(e) =>
                    setOwner({ ...owner, subDistrict: e.target.value })
                  }
                  fullWidth
                />
                <TextField
                  label="อำเภอ"
                  value={owner.district || ""}
                  onChange={(e) =>
                    setOwner({ ...owner, district: e.target.value })
                  }
                  fullWidth
                />
                <TextField
                  label="จังหวัด"
                  value={owner.province || ""}
                  onChange={(e) =>
                    setOwner({ ...owner, province: e.target.value })
                  }
                  fullWidth
                />
              </Box>

              <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                <Button variant="outlined" onClick={() => navigate("/owners")}>
                  ยกเลิก
                </Button>

                <Button variant="contained" onClick={handleUpdate}>
                  บันทึกการแก้ไข
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            สัตว์เลี้ยงของเจ้าของ ({pets.length})
          </Typography>

          <Box sx={{ height: 420 }}>
            <DataGrid
              rows={petRows}
              columns={petColumns}
              pageSizeOptions={[5, 10]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 5, page: 0 },
                },
              }}
              sx={{
                borderRadius: 2,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f5f5f5",
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default OwnerEdit;
